// ./src/components/features/EventGuests/PersonPicker.tsx
//
// Pick an existing contact (person) to add to an event, or create a brand-new
// one. A searchable Select queries PersonService as you type (debounced); a
// pinned "＋ Create new contact" option opens the shared ContactEditDrawer and
// selects the created person on save. Emits the chosen Person via onSelect.
//
// On phones the dropdown is replaced by the full-screen OptionPickerModal (same
// server-side search, big tap rows, the create action pinned on top) — a contact
// list is long and a cramped combobox is painful to use with a thumb.

import React, { useEffect, useMemo, useState } from "react";
import { SelectProps, Loader, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import { Person, PersonService, getPersonDisplayName } from "@/utils/api";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";
import { PersonGroupsProvider, useAppEnvironment } from "@/contexts";
import { AppIcon, OptionPickerModal, SelectField } from "@/components/generic";
import { ContactEditDrawer } from "@/components/features/Contacts/ContactEditDrawer/ContactEditDrawer";
import { useCreateInPlace } from "@/hooks/useCreateInPlace";
import { CREATE_IN_OWN_ACCOUNT } from "@/utils/createInPlace";

export interface PersonPickerProps extends Omit<SelectProps, "data" | "value" | "onChange" | "onSelect"> {
  /** Currently selected person id (controlled). */
  value?: string | null;
  /** Called with the chosen person (or null when cleared). */
  onSelect: (person: Person | null) => void;
  /** Person ids already on the event — shown greyed with an "added" hint and unselectable. */
  excludeIds?: string[];
  /** Notifies the host when the "create new contact" drawer opens/closes (to gate ⌘S). */
  onCreateOpenChange?: (open: boolean) => void;
}

export const PersonPicker: React.FC<PersonPickerProps> = ({
  value,
  onSelect,
  excludeIds = [],
  onCreateOpenChange,
  ...selectProps
}) => {
  const { t } = useTranslation(["events", "common"]);
  // #142 — a contact lands in the caller's own account: never offered to a crew member (it would be out of the event's reach).
  const canCreate = useCreateInPlace(CREATE_IN_OWN_ACCOUNT);
  const { isMobile } = useAppEnvironment();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search, 300);
  const [loading, setLoading] = useState(false);
  const [people, setPeople] = useState<Person[]>([]);
  const [createOpen, setCreateOpen] = useState(false);

  const excluded = useMemo(() => new Set(excludeIds.filter(Boolean)), [excludeIds]);

  // Load a page of contacts matching the current search (empty = most recent).
  useEffect(() => {
    let alive = true;
    setLoading(true);
    PersonService.list(
      debounced ? { searchKeyword: debounced } : {},
      { page: 1, pageSize: 25 },
      [{ name: "lastName", order: "ASC" }, { name: "firstName", order: "ASC" }]
    )
      .then((result) => {
        if (!alive) return;
        setPeople(result.code === OP_RESULT_CODES.OK && Array.isArray(result.data) ? (result.data as Person[]) : []);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [debounced]);

  // Keep the selected person visible even if it's not in the current search page.
  const selected = useMemo(() => people.find((p) => p.id === value) ?? null, [people, value]);

  const options = useMemo(() => {
    const items = people.map((p) => {
      const isExcluded = !!p.id && excluded.has(p.id) && p.id !== value;
      const base = getPersonDisplayName(p);
      const extra = [p.email, p.phone].filter(Boolean)[0];
      return {
        value: p.id as string,
        label: isExcluded
          ? t("events:guests.picker.alreadyAdded", { defaultValue: "{{name}} · already added", name: base })
          : extra
          ? `${base} · ${extra}`
          : base,
        disabled: isExcluded,
      };
    });
    // Keep the selected person visible even when it's off the current page.
    if (selected && value && !items.some((o) => o.value === value)) {
      items.unshift({ value, label: getPersonDisplayName(selected), disabled: false });
    }
    return items;
  }, [people, excluded, value, selected, t]);

  const setCreate = (open: boolean) => {
    setCreateOpen(open);
    onCreateOpenChange?.(open);
  };

  const handleChange = (val: string | null) => {
    if (!val) {
      onSelect(null);
      return;
    }
    const person = people.find((p) => p.id === val) ?? null;
    onSelect(person);
  };

  const selectedLabel = selected ? getPersonDisplayName(selected) : "";

  const contactPicker = isMobile ? (
    <>
      <TextInput
        label={selectProps.label}
        description={selectProps.description}
        placeholder={selectProps.placeholder}
        value={selectedLabel}
        readOnly
        onClick={() => setPickerOpen(true)}
        error={selectProps.error}
        rightSection={loading ? <Loader size="sm" /> : <AppIcon iconName="ChevronDown" size={18} />}
        size="md"
        styles={{ input: { cursor: "pointer", caretColor: "transparent" } }}
      />

      <OptionPickerModal
        opened={pickerOpen}
        onClose={() => setPickerOpen(false)}
        data={options}
        value={value ?? null}
        onSelect={handleChange}
        title={selectProps.label ?? t("events:guests.picker.title", { defaultValue: "Pick a contact" })}
        searchPlaceholder={t("events:guests.picker.search", { defaultValue: "Search contacts…" })}
        searchValue={search}
        onSearchChange={setSearch}
        loading={loading}
        clearable
        clearLabel={t("events:guests.picker.clear", { defaultValue: "No contact" })}
        createLabel={canCreate ? t("events:guests.picker.createLabel", { defaultValue: "Create new contact" }) : undefined}
        onCreate={
          canCreate
            ? () => {
                setPickerOpen(false);
                setCreate(true);
              }
            : undefined
        }
        nothingFoundMessage={t("events:guests.picker.noMatch", { defaultValue: "No contacts found" })}
      />
    </>
  ) : (
    <SelectField
      comboboxProps={DRAWER_COMBOBOX_PROPS}
      searchable
      clearable
      data={options}
      value={value ?? null}
      onChange={(_n, v) => handleChange(v)}
      createLabel={canCreate ? t("events:guests.picker.createLabel", { defaultValue: "Create new contact" }) : undefined}
      onCreate={canCreate ? () => setCreate(true) : undefined}
      searchValue={search}
      onSearchChange={setSearch}
      // Server-search: results are already filtered by the query, so don't let
      // Mantine ALSO client-filter them by the input text (which, once a contact is
      // selected, is that contact's name → it would hide every other contact).
      filter={({ options }) => options}
      // Opening with a contact selected: clear the query so the full recent list
      // loads instead of just the selected name's matches.
      onDropdownOpen={() => setSearch("")}
      nothingFoundMessage={
        loading
          ? t("common:loading", { defaultValue: "Loading…" })
          : t("events:guests.picker.noMatch", { defaultValue: "No contacts found" })
      }
      rightSection={loading ? <Loader size="md" /> : undefined}
      {...selectProps}
    />
  );

  return (
    <>
      {contactPicker}

      <PersonGroupsProvider>
        <ContactEditDrawer
          opened={createOpen}
          onClose={() => setCreate(false)}
          onCreate={(params) => PersonService.create(params)}
          onCreated={(person) => {
            setCreate(false);
            setPeople((prev) => [person, ...prev.filter((p) => p.id !== person.id)]);
            onSelect(person);
          }}
        />
      </PersonGroupsProvider>
    </>
  );
};
